/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/

import { isNumber, VisionApplication } from '../../application';
import { Measurement } from '../../components/measurement';
import { VisionLayout } from '../../layout';
import { VisionMapGeofence } from '../../map/geofence';
import { RockingDashboard } from './dashboard';
import { VisionEditRockSegmentWindow } from './edit_segment.window';
import { RockingMap } from './map';
import { VisionRockProgramWindow } from './rock_program.window';
import { VisionRockProgramWindow2 } from './rock_program.window2';
import { RockingProgramDTO, RockingProgramSegmentDTO, RockingSegmentDTO, VisionRockingService } from './rocking.service';

export class VisionRockingTab {
    layout!: dhtmlXLayoutObject;
    completeGrid!: dhtmlXGridObject;
    toolbar!: dhtmlXToolbarObject;

    map!: RockingMap;
    service: VisionRockingService;

    private programCache: Map<string, RockingProgramDTO>;
    private segmentCache: Map<string, RockingSegmentDTO>;

    private geofences!: Map<string, VisionMapGeofence>;
    private markers!: Map<string, google.maps.marker.AdvancedMarkerElement>;
    private programTree!: dhtmlXGridObject;
    private currentProgram: RockingProgramDTO | null = null;
    private initComplete = false;

    private dashboard!: RockingDashboard;

    private bounds!: google.maps.LatLngBounds;
    private labelsOn: boolean;

    constructor(vision: VisionLayout) {
        const config: IdhtmlXLayoutConfig = {
            parent: document.body,
            pattern: '3L',
        };

        this.segmentCache = new Map<string, RockingSegmentDTO>();
        this.geofences = new Map<string, VisionMapGeofence>();
        this.markers = new Map<string, google.maps.marker.AdvancedMarkerElement>();
        this.programCache = new Map<string, RockingProgramDTO>();
        this.labelsOn = false;

        this.layout = vision.tabbar.cells('ROCKING').attachLayout(config);
        this.service = new VisionRockingService();
        this.buildGUI();
        this.setupEvents();
    }

    public onSelect() {
        if (this.initComplete == false) {
            this.initComplete = true;
            const app = VisionApplication.getInstance();

            this.map.map.init('dad62ca70e7e3c4b6f8cbf8c', app.organizationService.dto?.latitude, app.organizationService.dto?.longitude).then(() => {
                const legend = document.getElementById('legend-rocking');
                this.map.map.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(legend as HTMLElement);

                this.getSegments();
                this.createMapToolbar();
            });
        }
    }

    private buildGUI(): void {
        this.layout.cells('a').setText('Program');

        this.toolbar = this.layout.cells('a').attachToolbar();
        this.toolbar.addButtonSelect('programs', 1, 'Programs', [], '', '', false, false, 0, '');
        this.toolbar.addButton('new', 1, 'Manage Segments', 'icons/connections.png', '');
        this.toolbar.addButton('manage_program', 1, 'Manage Program', 'icons/event.png', '');

        this.map = new RockingMap(this);
        this.dashboard = new RockingDashboard(this);
        this.layout.setSizes();

        this.programTree = this.layout.cells('a').attachGrid();
        this.programTree.setHeader('Segment,Required(tons),Total(tons),%', '', ['text-align:left;', 'text-align:center;', 'text-align:center;', 'text-align:center;']);

        this.programTree.setColTypes('tree,ed,ed,ron');
        this.programTree.setColAlign('left,center,center,center');
        this.programTree.setImagesPath('css/imgs/rocking_tree/');
        this.programTree.setEditable(true);
        this.programTree.init();

        this.layout.cells('a').hideHeader();
    }

    /** Reloads the data for the current program. Called after edits to segments or programs to refresh the data on the screen.
     *
     */
    public refresh(): void {
        this.getSegments();
        if (this.currentProgram) {
            this.showProgram(this.currentProgram.id.id);
        }
    }

    private populatePrograms(): void {
        this.programCache.clear();
        this.toolbar.forEachListOption('programs', (id) => {
            this.toolbar.removeListOption('programs', id);
        });

        const request = this.service.getPrograms();
        request.then((data) => {
            data.forEach((program) => {
                this.programCache.set(program.id.id, program);
                this.toolbar.addListOption('programs', program.id.id, 1, 'button', program.id.name, 'css/imgs/rocking_tree/leaf.gif');
            });
        });
    }

    private getSegments(): void {
        this.segmentCache.clear();

        const request = this.service.cachedFetchData();
        request.then((data) => {
            data.forEach((segment) => {
                this.segmentCache.set(segment.id.id, segment);
            });
            this.populatePrograms();
        });
    }

    private setupEvents(): void {
        this.programTree.attachEvent('onEditCell', (stage, rowId, colIndex, newValue, oldValue) => {
            return this.editCellHandler(stage, rowId, colIndex, newValue, oldValue);
        });

        this.programTree.attachEvent('onRowDblClicked', (rowId, index) => {
            if (this.programTree.getUserData(rowId, 'type') == 'branch') {
                const state = this.programTree.getOpenState(rowId);
                if (state === true) {
                    // It's open, so close it
                    this.programTree.closeItem(rowId);
                } else {
                    // It's closed, so open it
                    this.programTree.openItem(rowId);
                }
                return false; // prevent default behavior, which is to toggle open state
            }

            if (index == 0 && this.programTree.getUserData(rowId, 'type') == 'leaf') {
                const geofence = this.geofences.get(rowId);
                if (geofence) {
                    this.map.map.map.fitBounds(geofence.bounds);
                }
                return false;
            }

            return true; // allow default behavior too, if any
        });

        this.toolbar.attachEvent('onClick', (id) => {
            this.toolbarHandler(id);
        });
    }

    private toolbarHandler(id: string) {
        switch (id) {
            case 'new': {
                //const window = new VisionEditRockSegmentWindow(this);
                new VisionEditRockSegmentWindow(this);
                break;
            }
            case 'manage_program': {
                //const manager_window = new VisionRockProgramWindow(this);
                new VisionRockProgramWindow2(this);
                break;
            }
            default: {
                this.showProgram(id);
            }
        }
    }

    private showProgram(programID: string): void {
        const program = this.programCache.get(programID);
        if (program == undefined) {
            return;
        }
        const toolbarElement = document.getElementById('toolbar-program-name');
        if (toolbarElement) {
            toolbarElement.textContent = program.id.name;
        }

        this.currentProgram = program;

        this.dashboard.updateCountdown(program.end);

        this.programTree.clearAll(false);

        const folders = [...program.folders];
        const organizedList = new Array<string>();

        const mapped = new Map<string, RockingProgramSegmentDTO>();
        program.folders?.forEach((groupData) => {
            mapped.set(groupData.id, groupData);
        });

        while (folders.length > 0) {
            const data = folders.shift();
            if (data) {
                if (data.parentId == null) {
                    if (organizedList.includes(data.id) == false) {
                        organizedList.push(data.id);
                    }
                } else {
                    if (organizedList.includes(data.parentId)) {
                        organizedList.push(data.id);
                    } else {
                        folders.push(data);
                    }
                }
            }
        }

        let rootId = '0';
        organizedList?.forEach((id) => {
            const groupData = mapped.get(id);
            if (groupData) {
                let id = groupData.parentId;
                if (id == null) {
                    id = '0';
                    rootId = groupData.id;
                }

                //const name = groupData.name == null ? 'N/A' : groupData.name;
                this.programTree.addRow(groupData.id, [groupData.name], 0, groupData.parentId, 'folder.gif', true);
                this.programTree.setUserData(groupData.id, 'type', 'branch');
            }
        });

        let i = 0;
        const required = 0;
        const total = 0;
        this.bounds = new google.maps.LatLngBounds();
        this.markers.forEach((marker) => {
            marker.map = null;
        });
        this.geofences.forEach((geofence) => {
            geofence.polygon.setMap(null);
        });
        this.geofences.clear();
        this.markers.clear();

        const parentTotals: Map<string, { requiredKg: number; total: number }> = new Map<string, { requiredKg: number; total: number }>();

        let totalProgramRequiredKg = 0;
        let totalDeliveredKg = 0;

        program.segments.forEach((segment) => {
            let segmentName = 'Segment Name';

            //segment.total = Math.floor(Math.random() * 300 * 2000);
            //total += segment.total;

            // If segment has a segmentId and it's in the cache, draw it on the map and get its name
            if (segment.segmentId && this.segmentCache.get(segment.segmentId)) {
                const segmentInfo = this.segmentCache.get(segment.segmentId);
                const nm = this.segmentCache.get(segment.segmentId)?.id.name;
                if (nm) {
                    segmentName = nm;
                }
                if (segmentInfo) {
                    let percent = 0;
                    if (segment.requiredKg && segment.total) {
                        percent = Math.round((segment.total / segment.requiredKg) * 100);
                    }

                    totalProgramRequiredKg += segment.requiredKg ? segment.requiredKg : 0;
                    totalDeliveredKg += segment.total ? segment.total : 0;

                    const geo = new VisionMapGeofence(this.map.map);
                    geo.createGeofence(segmentInfo.geometry, this.getColor(percent), 1);
                    geo.polygon.setMap(this.map.map.map);
                    geo.polygon.addListener('click', () => {
                        this.programTree.selectRowById(segment.id, false, true, false);
                    });
                    this.geofences.set(segment.id, geo);
                    this.bounds.union(geo.bounds);

                    const segmentName = document.createElement('div');
                    segmentName.className = 'segment-tag';
                    segmentName.textContent = nm ? nm : 'Segment Name';

                    const boundsCenter = geo.bounds.getCenter();
                    const marker = new google.maps.marker.AdvancedMarkerElement({
                        position: boundsCenter,
                        content: segmentName,
                    });
                    marker.addListener('click', () => {
                        this.programTree.selectRowById(segment.id, false, true, false);
                    });
                    this.markers.set(segment.id, marker);
                }
            }

            if (segment.segmentId) {
                const segmentInfo = this.segmentCache.get(segment.segmentId);
                let percent = 0;
                if (segment.requiredKg && segment.total) {
                    percent = Math.round((segment.total / segment.requiredKg) * 100);
                }

                const required = segment.requiredKg ? Measurement.kg2ImperialTons(segment.requiredKg) : 0;
                const total = Measurement.kg2ImperialTons(segment.total ? segment.total : 0);

                this.programTree.addRow(segment.id, [segmentName, required, total, percent], i++, segment.parentId, 'draw-polygon.png', false);
                this.programTree.cells(segment.id, 3).setTextColor(this.getColor(percent));

                this.programTree.setUserData(segment.id, 'type', 'leaf');
                this.programTree.setUserData(segment.id, 'segmentId', segment.segmentId);

                this.updateParent(segment?.requiredKg ? segment?.requiredKg : 0, segment.total ? segment.total : 0, segment.parentId, parentTotals);
            }
        });

        parentTotals.forEach((value, key) => {
            const percent = value.requiredKg != 0 ? Math.round((value.total / value.requiredKg) * 100) : 0;
            this.programTree.cells(key, 1).setValue(Measurement.kg2ImperialTons(value.requiredKg).toFixed(0));
            this.programTree.cells(key, 2).setValue(Measurement.kg2ImperialTons(value.total).toFixed(0));
            this.programTree.cells(key, 3).setValue(percent.toString());
            this.programTree.cells(key, 3).setTextColor(this.getColor(percent));
        });

        this.programTree.expandAll();
        this.dashboard.updateCountdown(program.end);
        this.dashboard.updateRockTotal(totalDeliveredKg, totalProgramRequiredKg);
        this.dashboard.updateTrend(totalDeliveredKg, totalProgramRequiredKg, program.start, program.end);

        // Fit the map to the bounds of the geofences and turn the labels on if they were on before
        this.map.map.map.fitBounds(this.bounds);
        this.toggleLabels(this.labelsOn);
    }

    private updateParent(requiredKg: number, total: number, parentId: string, parentTotals: Map<string, { requiredKg: number; total: number }>) {
        if (parentId == '0') {
            return;
        } else {
            const parentTotal = parentTotals.get(parentId);
            if (parentTotal) {
                parentTotal.requiredKg += requiredKg;
                parentTotal.total += total;
            } else {
                parentTotals.set(parentId, { requiredKg: requiredKg, total: total });
            }

            if (this.programTree.getParentId(parentId) != '0') {
                this.updateParent(requiredKg, total, this.programTree.getParentId(parentId), parentTotals);
            }
        }
    }

    getColor(percent: number): string {
        if (percent < 10) {
            return 'rgb(255, 0, 0)'; // Red
        } else if (percent >= 10 && percent < 50) {
            return 'rgb(255, 165, 0)'; // Orange
        } else if (percent >= 50 && percent < 90) {
            return 'rgb(255, 255, 0)'; // Yellow
        } else if (percent >= 90 && percent <= 100) {
            return 'rgb(0, 128, 0)'; // Green
        } else {
            return 'rgb(128, 0, 128)'; // Purple
        }
    }

    private createMapToolbar(): void {
        const toolbar = document.createElement('div');
        toolbar.style.display = 'flex';
        toolbar.style.alignItems = 'center';
        toolbar.style.gap = '10px'; // spacing between controls

        toolbar.style.background = '#fff';
        toolbar.style.border = '2px solid #fff';
        toolbar.style.borderRadius = '3px';
        toolbar.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
        toolbar.style.padding = '5px';

        // Add buttons
        const zoomAllBtn = document.createElement('button');
        zoomAllBtn.innerText = 'Zoom All';

        const controlButton = document.createElement('button');
        controlButton.className = 'btn';
        controlButton.innerHTML = '<i class="fa fa-search"></i>Toggle Labels';

        controlButton.addEventListener('click', () => {
            this.labelsOn = !this.labelsOn;
            this.toggleLabels(this.labelsOn);
        });

        const label = document.createElement('span');
        label.innerText = 'Program Name';
        label.id = 'toolbar-program-name';
        label.style.fontWeight = 'bold';
        label.style.fontSize = '1.2em';
        label.style.fontFamily = 'Arial, sans-serif';

        toolbar.appendChild(zoomAllBtn);
        toolbar.appendChild(controlButton);
        toolbar.appendChild(label);

        zoomAllBtn.onclick = () => {
            this.map.map.map.fitBounds(this.bounds);
        };
        // Add toolbar to map
        this.map.map.map.controls[google.maps.ControlPosition.TOP_LEFT].push(toolbar);
    }

    public toggleLabels(state: boolean): void {
        this.currentProgram?.segments.forEach((segment) => {
            const marker = this.markers.get(segment.id);
            if (marker == undefined) {
                return;
            }
            if (state == true) {
                marker.map = this.map.map.map;
            } else {
                marker.map = undefined;
            }
        });
    }

    private editCellHandler(stage: number, rowId: string, colIndex: number, newValue: string, oldValue: string): boolean {
        if (stage == 2) {
            if (this.currentProgram) {
                // Make sure the new value is a number if editing the required tons or total tons columns, which are column index 1 and 2 respectively
                if (isNumber(newValue) == false) {
                    dhtmlx.message({
                        title: 'error',
                        text: 'Not a valid number',
                        type: 'error',
                    });
                    return false;
                }

                // Only allow editing the required tons column, which is column index 1
                if (colIndex == 1) {
                    const program = this.programCache.get(this.currentProgram.id.id);
                    if (program) {
                        // eslint-disable-next-line @typescript-eslint/prefer-for-of
                        for (let i = 0; i < program.segments.length; i++) {
                            if (program.segments[i].id === rowId) {
                                program.segments[i].requiredKg = Measurement.imperialTons2Kg(Number(newValue));
                                this.programCache.set(program.id.id, program);
                                break;
                            }
                        }
                        this.showProgram(this.currentProgram.id.id);
                        this.toggleLabels(this.labelsOn);
                        this.service.updateProgramSegmentRequiredKg(rowId, Measurement.imperialTons2Kg(Number(newValue)));
                        return true;
                    }
                } else if (colIndex == 2) {
                    const program = this.programCache.get(this.currentProgram.id.id);
                    if (program) {
                        // eslint-disable-next-line @typescript-eslint/prefer-for-of
                        for (let i = 0; i < program.segments.length; i++) {
                            if (program.segments[i].id === rowId) {
                                program.segments[i].total = Measurement.imperialTons2Kg(Number(newValue));
                                this.programCache.set(program.id.id, program);
                                break;
                            }
                        }
                        this.showProgram(this.currentProgram.id.id);
                        this.toggleLabels(this.labelsOn);
                        this.service.updateProgramSegmentTotal(rowId, Measurement.imperialTons2Kg(Number(newValue)));
                        return true;
                    }
                }
            }
        }
        return true;
    }
}
